// src/components/CourseCatalog.tsx
import React, { useState } from 'react';
import styles from '../styles/CourseCatalog.module.css';

interface Course {
  id: string;
  code: string;
  title: string;
  description: string;
  lecturer: string;
  image: string;
  credits: number;
  prerequisites: string;
  schedule: string;
  objectives: string[];
}

const CourseCatalog: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'undergrad' | 'masters' | 'phd'>('undergrad');
  const [expandedCourse, setExpandedCourse] = useState<string | null>(null);

  const toggleCourse = (courseId: string) => {
    setExpandedCourse(expandedCourse === courseId ? null : courseId);
  };

  const undergraduateCourses: Course[] = [
    {
      id: 'phl101',
      code: 'PHL 101',
      title: 'Introduction to Logic',
      description: 'Fundamental principles of logical reasoning and argument analysis.',
      lecturer: 'Dr. Chief Nwanch',
      image: 'https://images.unsplash.com/photo-1589652717521-10c0d092dea9?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80',
      credits: 3,
      prerequisites: 'None',
      schedule: 'Mon & Wed, 10:00 AM – 11:30 AM',
      objectives: [
        'Understand basic logical structures',
        'Analyze arguments for validity',
        'Apply logical principles to philosophical problems',
        'Develop critical thinking skills',
      ],
    },
    {
      id: 'phl102',
      code: 'PHL 102',
      title: 'Ethics and Moral Philosophy',
      description: 'Examination of major ethical theories and moral dilemmas.',
      lecturer: 'Dr. Arjun Sharma',
      image: 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?ixlib=rb-4.0.3&auto=format&fit=crop&w=1468&q=80',
      credits: 3,
      prerequisites: 'None',
      schedule: 'Tue & Thu, 1:00 PM – 2:30 PM',
      objectives: [
        'Compare major ethical theories',
        'Analyze contemporary moral issues',
        'Develop personal ethical framework',
        'Apply ethical reasoning to real-world situations',
      ],
    },
    {
      id: 'phl201',
      code: 'PHL 201',
      title: 'Ancient Philosophy',
      description: 'Study of philosophical traditions from ancient civilizations.',
      lecturer: 'Prof. Michael Touw',
      image: 'https://images.unsplash.com/photo-1605100804763-247f67b3557e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80',
      credits: 4,
      prerequisites: 'PHL 101',
      schedule: 'Mon, Wed, Fri, 9:00 AM – 10:00 AM',
      objectives: [
        'Understand Pre-Socratic philosophy',
        'Analyze Socratic dialogues',
        'Explore Platonic and Aristotelian systems',
        'Examine Hellenistic philosophy',
      ],
    },
  ];

  const mastersCourses: Course[] = [
    {
      id: 'phl501',
      code: 'PHL 501',
      title: 'Advanced Metaphysics',
      description: 'In-depth exploration of contemporary metaphysical problems.',
      lecturer: 'Dr. Alexa Baldo',
      image: 'https://images.unsplash.com/photo-1498243691581-b145c3f54a5a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80',
      credits: 4,
      prerequisites: 'Undergraduate Metaphysics course',
      schedule: 'Tue & Thu, 3:00 PM – 4:30 PM',
      objectives: [
        'Examine theories of existence and reality',
        'Analyze contemporary debates in metaphysics',
        'Develop original metaphysical arguments',
        'Critique foundational metaphysical assumptions',
      ],
    },
    {
      id: 'phl502',
      code: 'PHL 502',
      title: 'Philosophy of Science',
      description: 'Critical examination of scientific methodology and epistemology.',
      lecturer: 'Dr. David O. Ekarem',
      image: 'https://images.unsplash.com/photo-1581094794329-16b125e1d71e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80',
      credits: 4,
      prerequisites: 'Undergraduate Philosophy or Science course',
      schedule: 'Mon & Wed, 2:00 PM – 3:30 PM',
      objectives: [
        'Understand scientific methodology',
        'Analyze scientific revolutions',
        'Explore realism vs. anti-realism debates',
        'Examine ethics in scientific research',
      ],
    },
  ];

  const phdCourses: Course[] = [
    {
      id: 'phl801',
      code: 'PHL 801',
      title: 'Contemporary Ethical Theory',
      description: 'Advanced seminar on current developments in ethical theory.',
      lecturer: 'Dr. Adana Okoto',
      image: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80',
      credits: 5,
      prerequisites: 'Masters in Philosophy or related field',
      schedule: 'Fri, 9:00 AM – 12:00 PM',
      objectives: [
        'Critique contemporary ethical frameworks',
        'Develop original contributions to ethical theory',
        'Analyze ethical issues in global contexts',
        'Prepare research for academic publication',
      ],
    },
    {
      id: 'phl802',
      code: 'PHL 802',
      title: 'Philosophy of Artificial Intelligence',
      description: 'Exploration of philosophical implications of AI and machine learning.',
      lecturer: 'Dr. Kwame Wrumsith',
      image: 'https://images.unsplash.com/photo-1677442135722-5f8ea49cec8a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80',
      credits: 5,
      prerequisites: 'Background in Philosophy and Computer Science',
      schedule: 'Tue, 1:00 PM – 4:00 PM',
      objectives: [
        'Examine consciousness in AI systems',
        'Analyze ethical implications of AI',
        'Explore epistemology of machine learning',
        'Develop philosophical frameworks for AI ethics',
      ],
    },
  ];

  const getCourses = () => {
    if (activeTab === 'masters') return mastersCourses;
    if (activeTab === 'phd') return phdCourses;
    return undergraduateCourses;
  };

  const getDegreeTitle = () => {
    if (activeTab === 'masters') return 'Masters Program';
    if (activeTab === 'phd') return 'PhD Program';
    return 'Undergraduate Program';
  };

  return (
    <div className={styles['course-catalog']}>
      {/* Header */}
      <header className={styles['catalog-header']}>
        <div className={styles['header-overlay']}>
          <div className={styles['header-content']}>
            <h1>Department of Philosophy Course Catalog</h1>
            <p>
              Explore our comprehensive curriculum designed to develop critical thinking
              and philosophical insight
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={styles['catalog-container']}>
        {/* Tabs */}
        <div className={styles['degree-tabs']}>
          <button
            className={activeTab === 'undergrad' ? styles.active : ''}
            onClick={() => setActiveTab('undergrad')}
          >
            Undergraduate
          </button>
          <button
            className={activeTab === 'masters' ? styles.active : ''}
            onClick={() => setActiveTab('masters')}
          >
            Masters
          </button>
          <button
            className={activeTab === 'phd' ? styles.active : ''}
            onClick={() => setActiveTab('phd')}
          >
            PhD
          </button>
        </div>

        {/* Degree header */}
        <div className={styles['degree-header']}>
          <h2>{getDegreeTitle()}</h2>
          <div className={styles.divider} />
        </div>

        {/* Search */}
        <div className={styles['search-container1']}>
          <input type="text" placeholder="Search courses, lecturers, topics..." />
          <button>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5z" />
            </svg>
          </button>
        </div>

        {/* Courses Grid */}
        <div className={styles['courses-grid']}>
          {getCourses().map(course => (
            <div
              key={course.id}
              className={`${styles['course-card']} ${expandedCourse === course.id ? styles.expanded : ''}`}
              onClick={() => toggleCourse(course.id)}
            >
              <div
                className={styles['course-image']}
                style={{ backgroundImage: `url(${course.image})` }}
              />
              <div className={styles['course-content']}>
                <div className={styles['course-code']}>{course.code}</div>
                <h3>{course.title}</h3>
                <p className={styles['course-lecturer']}>Lecturer: {course.lecturer}</p>
                <p className={styles['course-description']}>{course.description}</p>

                {expandedCourse === course.id && (
                  <div className={styles['course-details']}>
                    <div className={styles['detail-row']}>
                      <span>Credits:</span>
                      <span>{course.credits}</span>
                    </div>
                    <div className={styles['detail-row']}>
                      <span>Prerequisites:</span>
                      <span>{course.prerequisites}</span>
                    </div>
                    <div className={styles['detail-row']}>
                      <span>Schedule:</span>
                      <span>{course.schedule}</span>
                    </div>
                    <div className={styles['learning-objectives']}>
                      <h4>Learning Objectives:</h4>
                      <ul>
                        {course.objectives.map((obj, idx) => (
                          <li key={idx}>{obj}</li>
                        ))}
                      </ul>
                    </div>
                    <button className={styles['enroll-btn']}>Enroll in Course</button>
                  </div>
                )}

                <div className={styles['course-footer']}>
                  <div className={styles.credits}>{course.credits} Credits</div>
                  <div className={styles['expand-icon']}>
                    {expandedCourse === course.id ? '−' : '+'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Student Association */}
        <section className={styles['student-association']}>
          <div className={styles['section-header']}>
            <h2>Philosophy Student Association</h2>
            <div className={styles.divider} />
          </div>
          <div className={styles['association-content']}>
            <div className={styles['association-text']}>
              <h3>Join Our Vibrant Community</h3>
              <p>
                The Philosophy Student Association at the University of Calabar is dedicated to fostering
                a lively academic and social environment for all philosophy students.
              </p>
              <div className={styles['benefits-grid']}>
                <div className={styles.benefit}>
                  <div className={styles['benefit-icon']}>🎓</div>
                  <h4>Academic Workshops</h4>
                  <p>Access to specialized workshops and seminars</p>
                </div>
                <div className={styles.benefit}>
                  <div className={styles['benefit-icon']}>🤝</div>
                  <h4>Networking Events</h4>
                  <p>Connect with faculty, alumni, and professionals</p>
                </div>
                <div className={styles.benefit}>
                  <div className={styles['benefit-icon']}>💬</div>
                  <h4>Discussion Groups</h4>
                  <p>Engage in philosophical dialogues and debates</p>
                </div>
                <div className={styles.benefit}>
                  <div className={styles['benefit-icon']}>🔍</div>
                  <h4>Research Support</h4>
                  <p>Resources and mentorship for philosophical research</p>
                </div>
              </div>
              <button className={styles['join-btn']}>Join the Association</button>
            </div>
            <div className={styles['association-image']} />
          </div>
        </section>

        {/* Fellowships & Funding */}
        <section className={styles.fellowships}>
          <div className={styles['section-header']}>
            <h2>Fellowships & Funding Opportunities</h2>
            <div className={styles.divider} />
          </div>
          <div className={styles['fellowships-grid']}>
            <div className={styles['funding-card']}>
              <h3>Ethics in AI Scholarship</h3>
              <p>Support for students researching ethical dimensions of artificial intelligence.</p>
              <button>Apply Now →</button>
            </div>
            <div className={styles['funding-card']}>
              <h3>Undergraduate Research Grant</h3>
              <p>Funding for undergraduate students pursuing philosophical research projects.</p>
              <button>Apply Now →</button>
            </div>
            <div className={styles['funding-card']}>
              <h3>Graduate Fellowship</h3>
              <p>Comprehensive support for exceptional PhD candidates in philosophy.</p>
              <button>Apply Now →</button>
            </div>
            <div className={styles['funding-card']}>
              <h3>Community Engagement Award</h3>
              <p>Recognition for students applying philosophical insights to community service.</p>
              <button>Apply Now →</button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default CourseCatalog;
